import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { getCurrentFY } from '@/lib/fy';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { PencilSimple, Trash, ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import FinancePage from '@/pages/FinancePage';
import InvoiceViewDialog from '@/components/InvoiceViewDialog';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

export default function ReportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Transactions state
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [filterAccount, setFilterAccount] = useState('all');
  const [filterType, setFilterType] = useState('all');
  // Default the Day-Book filter to TODAY so the page loads fast with the
  // current day's entries — the most frequently checked slice. Users can
  // widen with FY / Week / Month / All / custom dates at the top.
  const _todayIso = new Date().toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(_todayIso);
  const [toDate, setToDate] = useState(_todayIso);
  const [datePreset, setDatePreset] = useState('today');
  const applyDatePreset = (val) => {
    setDatePreset(val);
    const today = new Date();
    const toIso = (d) => d.toISOString().split('T')[0];
    if (val === 'all') { setFromDate(''); setToDate(''); return; }
    if (val === 'fy')    { const fy = getCurrentFY(); setFromDate(fy.start); setToDate(fy.end); return; }
    if (val === 'today') { const d = toIso(today); setFromDate(d); setToDate(d); return; }
    if (val === 'week') {
      const start = new Date(today); start.setDate(start.getDate() - start.getDay());
      setFromDate(toIso(start)); setToDate(toIso(today)); return;
    }
    if (val === 'month') {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      setFromDate(toIso(start)); setToDate(toIso(today)); return;
    }
  };
  const [editTxn, setEditTxn] = useState(null);
  const [showEditTxn, setShowEditTxn] = useState(false);
  const [editTxnForm, setEditTxnForm] = useState({ type: 'income', category: '', amount: '', account: '', description: '', date: '' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Bulk-delete selection — Set of txn ids ticked for batch removal.
  // Only `deletable` rows can be ticked (admin-only finance txns + transfers).
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  // Bill number quick-view — clicking an INV-prefixed voucher in the Day
  // Book table pops a fullscreen view of that invoice with an Edit shortcut.
  const [viewBillNumber, setViewBillNumber] = useState(null);

  const fetchTxns = useCallback(() => {
    const params = {};
    if (filterAccount !== 'all') params.account = filterAccount;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    // Opening balance only needs from_date + account — closing balance is
    // derived client-side from opening + visible debits/credits so the endpoint
    // stays read-only and cheap.
    const openingParams = {};
    if (fromDate) openingParams.from_date = fromDate;
    if (filterAccount !== 'all') openingParams.account = filterAccount;
    Promise.all([
      api.get('/finance', { params }),
      api.get('/finance/accounts'),
      fromDate ? api.get('/reports/day-book-balance', { params: openingParams }) : Promise.resolve({ data: { opening_balance: 0 } }),
    ]).then(([t, a, ob]) => {
      setTransactions(t.data);
      setAccounts(a.data);
      setOpeningBalance(ob.data?.opening_balance || 0);
    }).catch(console.error);
  }, [filterAccount, fromDate, toDate]);

  useEffect(() => { fetchTxns(); }, [fetchTxns]);

  const accNameById = (id) => accounts.find(a => a.id === id)?.name || id;

  const openEditTxn = (t) => {
    setEditTxn(t);
    setEditTxnForm({ type: t.type, category: t.category, amount: String(t.amount), account: t.account, description: t.description || '', date: t.created_at ? t.created_at.split('T')[0] : '' });
    setShowEditTxn(true);
  };

  const handleEditTxn = async () => {
    if (!editTxn) return;
    if (!editTxnForm.amount) { toast.error('Amount is required'); return; }
    try {
      await api.put(`/finance/${editTxn.id}`, {
        type: editTxnForm.type, category: editTxnForm.category,
        amount: parseFloat(editTxnForm.amount), account: editTxnForm.account, description: editTxnForm.description,
      });
      toast.success('Transaction updated');
      setShowEditTxn(false); setEditTxn(null); fetchTxns();
    } catch { toast.error('Failed to update'); }
  };

  const confirmDeleteTxn = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/finance/${confirmDelete.id}`);
      toast.success('Transaction deleted');
      fetchTxns();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
    setConfirmDelete(null);
  };

  // Bulk delete — loops through ticked rows, continues on errors so a
  // single bad row doesn't block the rest.
  const handleBulkDelete = async () => {
    const ids = Array.from(bulkSelected);
    if (ids.length === 0) return;
    let success = 0, failed = 0;
    for (const id of ids) {
      try { await api.delete(`/finance/${id}`); success++; }
      catch { failed++; }
    }
    if (success) toast.success(`Deleted ${success} transaction${success > 1 ? 's' : ''}`);
    if (failed) toast.error(`${failed} could not be deleted`);
    setBulkSelected(new Set());
    setConfirmBulkDelete(false);
    fetchTxns();
  };

  // Apply type filter client-side. We always exclude `advance_used` from the
  // Day Book — applying an advance against an invoice doesn't move cash
  // (the cash was already received earlier), so it shouldn't show as a
  // separate credit row. Same for any income / govt_fee_collected legs that
  // were stamped `from_advance: true` — those are reclassification audit
  // rows, not actual bank deposits. The customer ledger captures the
  // disposition by appending the bill number to the original advance
  // receipt's description.
  const visibleTxns = transactions.filter(t => {
    if (t.type === 'advance_used') return false;
    if (t.from_advance) return false;
    if (filterType === 'all') return true;
    if (filterType === 'transfer') return t.type === 'transfer_in' || t.type === 'transfer_out';
    if (filterType === 'govt_fee') return t.type === 'govt_fee' || t.type === 'govt_fee_collected';
    if (filterType === 'service_charge') return t.type === 'income' && !!t.reference_id;
    if (filterType === 'income') return t.type === 'income' && !t.reference_id;
    return t.type === filterType;
  });

  // Build the Day Book rows. Invoice receipts (income + govt_fee_collected
  // sharing the same reference_id + account) are MERGED back into ONE debit
  // row showing the TOTAL — that matches the single bank-deposit figure the
  // operator sees on a statement. The component breakdown (Svc ₹X + GF ₹Y)
  // is appended to the description so visibility is preserved without
  // double-counting cash flow. Filter pills (Service Charge / Govt Fee) still
  // work because demoting a merged row down to its single matching member
  // happens below.
  const debitTypes = new Set(['income', 'govt_fee_collected', 'transfer_in', 'opening_balance', 'advance_received']);
  const creditTypes = new Set(['expense', 'govt_fee', 'transfer_out', 'advance_used']);
  const fmtMoney = (n) => `₹${(Number(n) || 0).toFixed(2)}`;
  const dayBookRows = (() => {
    const map = new Map();
    const order = [];
    for (const t of visibleTxns) {
      const canMerge = (t.type === 'income' || t.type === 'govt_fee_collected') && t.reference_id;
      if (canMerge) {
        const key = `inv:${t.reference_id}:${t.account}`;
        const invMatch = (t.description || '').match(/for\s+(INV-\S+|T-\S+|DRAFT-\S+)/i);
        const invNo = t.voucher_number || (invMatch ? invMatch[1] : '');
        const existing = map.get(key);
        const isSvc = t.type === 'income';
        if (!existing) {
          map.set(key, {
            id: key,
            type: 'invoice_received',
            category: 'Invoice Received',
            account: t.account,
            description: '',  // filled in below from svc + gf parts
            amount: Number(t.amount) || 0,
            created_at: t.created_at,
            reference_id: t.reference_id,
            voucher_number: invNo,
            _merged: true,
            _svc: isSvc ? Number(t.amount) || 0 : 0,
            _gf: !isSvc ? Number(t.amount) || 0 : 0,
            _members: [t],
          });
          order.push(key);
        } else {
          existing.amount += Number(t.amount) || 0;
          if (isSvc) existing._svc += Number(t.amount) || 0;
          else existing._gf += Number(t.amount) || 0;
          existing._members.push(t);
          if ((t.created_at || '') < (existing.created_at || '')) existing.created_at = t.created_at;
          if (!existing.voucher_number && invNo) existing.voucher_number = invNo;
        }
        continue;
      }
      const key = `single:${t.id}`;
      let billNo = t.voucher_number || '';
      if (!billNo && t.description) {
        const m = t.description.match(/for\s+(INV-\S+|T-\S+|DRAFT-\S+)/i);
        if (m) billNo = m[1];
      }
      map.set(key, { ...t, voucher_number: billNo, _merged: false });
      order.push(key);
    }
    return order.map(k => {
      const row = map.get(k);
      // Demote a merged row when only ONE of its members survived the filter
      // (e.g. user picked Service Charge — only the income txn remained).
      // We collapse back to the raw single-component row so badges read
      // 'service charge' or 'govt fee' instead of 'invoice'.
      if (row._merged && row._members && row._members.length === 1) {
        const m = row._members[0];
        let billNo = m.voucher_number || row.voucher_number || '';
        if (!billNo && m.description) {
          const mm = m.description.match(/for\s+(INV-\S+|T-\S+|DRAFT-\S+)/i);
          if (mm) billNo = mm[1];
        }
        return {
          ...m,
          voucher_number: billNo,
          _merged: false,
          debit: Number(m.amount) || 0,
          credit: 0,
        };
      }
      let debit = 0, credit = 0;
      if (row._merged) {
        debit = row.amount;
        // Build a one-line component breakdown for the merged row.
        // Pattern: "Received for INV-12345 (Svc ₹150.00 + GF ₹50.00)"
        const parts = [];
        if (row._svc > 0) parts.push(`Svc ${fmtMoney(row._svc)}`);
        if (row._gf > 0) parts.push(`GF ${fmtMoney(row._gf)}`);
        const breakdown = parts.length ? ` (${parts.join(' + ')})` : '';
        row.description = `Received for ${row.voucher_number || 'invoice'}${breakdown}`;
      } else if (row.type === 'adjustment') {
        if (row.direction === 'credit') debit = row.amount; else credit = row.amount;
      } else if (debitTypes.has(row.type)) {
        debit = row.amount;
      } else if (creditTypes.has(row.type)) {
        credit = row.amount;
      }
      return { ...row, debit, credit };
    });
  })();

  // Row-deletable helper (kept in sync with body-row logic). Drives both
  // the Select-All master checkbox and individual row checkboxes so we
  // never let the user tick a row that the API will reject.
  const isRowDeletable = (t) => {
    const isMerged = t._merged;
    const isTransfer = t.type === 'transfer_in' || t.type === 'transfer_out';
    const isGovtFee = t.type === 'govt_fee';
    const isGovtFeeCollected = t.type === 'govt_fee_collected';
    const isServiceCharge = t.type === 'income' && !!t.reference_id;
    const editable = !isMerged && !isServiceCharge && !isGovtFeeCollected && !isGovtFee;
    return editable && (isAdmin || isTransfer);
  };
  const deletableIds = dayBookRows.filter(isRowDeletable).map(r => r.id);
  const allDeletableSelected = deletableIds.length > 0 && deletableIds.every(id => bulkSelected.has(id));
  const toggleSelectAll = () => {
    if (allDeletableSelected) {
      setBulkSelected(new Set());
    } else {
      setBulkSelected(new Set(deletableIds));
    }
  };
  const toggleRow = (id) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="reports-page">
      {/* Balance Sheet — full Liabilities & Assets snapshot, rendered at the top
          of the Reports page. Pulled in from FinancePage so the two stay in
          lockstep. The dedicated /finance route was retired in this change. */}
      <FinancePage />

      {/* Day Book — primary focus */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between border-b-2 border-[#0A0A0A] pb-2">
          <div>
            <h2 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Day Book</h2>
            <p className="text-xs text-[#52525B]">Chronological debit / credit register — invoice receipts merged to one debit line (matches the bank deposit), with Service Charge + Govt Fee breakdown shown in the description</p>
          </div>
          <Badge className="rounded-md text-xs bg-[#0A0A0A] text-white border-0 px-2.5 py-1" variant="outline">{dayBookRows.length} entries</Badge>
        </div>

        {/* Filters — Account row hidden per user request; type + date controls only */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1.5 flex-wrap">
            {[
              { val: 'all', label: 'All Types' },
              { val: 'service_charge', label: 'Service Charge' },
              { val: 'govt_fee', label: 'Govt Fee' },
              { val: 'income', label: 'Income (Other)' },
              { val: 'expense', label: 'Expense' },
              { val: 'transfer', label: 'Transfers' },
              { val: 'adjustment', label: 'Adjustment' },
            ].map(o => (
              <Button key={o.val} variant={filterType === o.val ? 'default' : 'outline'} size="sm" className={`rounded-lg text-xs ${filterType === o.val ? 'bg-[#0A0A0A] text-white hover:bg-black/80 hover:text-white' : 'border-[#E4E4E7]'}`}
                onClick={() => setFilterType(o.val)} data-testid={`filter-type-${o.val}`}>{o.label}</Button>
            ))}
          </div>
          <div className="h-5 w-px bg-[#E4E4E7]" />
          <div className="flex gap-1.5 flex-wrap">
            {[
              { val: 'fy',    label: 'FY' },
              { val: 'today', label: 'Today' },
              { val: 'week',  label: 'This Week' },
              { val: 'month', label: 'This Month' },
              { val: 'all',   label: 'All' },
            ].map(p => (
              <Button key={p.val} variant={datePreset === p.val ? 'default' : 'outline'} size="sm"
                className={`rounded-lg text-xs ${datePreset === p.val ? 'bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white' : 'border-[#E4E4E7]'}`}
                onClick={() => applyDatePreset(p.val)}
                data-testid={`date-preset-${p.val}`}>{p.label}</Button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setDatePreset('custom'); }} className="h-7 rounded-lg text-xs w-32" data-testid="reports-from-date" />
            <span className="text-xs text-[#52525B]">to</span>
            <Input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setDatePreset('custom'); }} className="h-7 rounded-lg text-xs w-32" data-testid="reports-to-date" />
          </div>
        </div>

        {/* Bulk action toolbar — shows only when at least one row is ticked. */}
        {bulkSelected.size > 0 && (
          <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-[#FEF2F2] border border-[#FCA5A5]" data-testid="bulk-action-toolbar">
            <div className="text-sm text-[#991B1B] font-medium">
              <span data-testid="bulk-selected-count">{bulkSelected.size}</span> transaction{bulkSelected.size > 1 ? 's' : ''} selected
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="h-8 rounded-lg text-xs text-[#52525B]" onClick={() => setBulkSelected(new Set())} data-testid="bulk-clear-btn">Clear</Button>
              <Button size="sm" className="h-8 rounded-lg bg-[#EF4444] text-white hover:bg-[#DC2626] text-xs" onClick={() => setConfirmBulkDelete(true)} data-testid="bulk-delete-btn">
                <Trash size={12} className="mr-1" /> Delete Selected
              </Button>
            </div>
          </div>
        )}

        <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
          <CardContent className="p-0">
            {dayBookRows.length === 0 && !fromDate ? <p className="p-8 text-center text-sm text-[#52525B]">No transactions match the current filters</p> : (
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E4E4E7] bg-[#FAFAFA]">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allDeletableSelected}
                        onCheckedChange={toggleSelectAll}
                        disabled={deletableIds.length === 0}
                        aria-label="Select all deletable transactions"
                        data-testid="daybook-select-all"
                      />
                    </TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold whitespace-nowrap">Date</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Bill / Voucher</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Voucher Type</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Category</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Account</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Description</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Debit</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Credit</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Opening balance pseudo-row — only when a from_date filter is active. */}
                  {fromDate && (
                    <TableRow className="border-[#E4E4E7] bg-[#F9FAFB]" data-testid="daybook-opening-row">
                      <TableCell />
                      <TableCell className="text-xs text-[#52525B] whitespace-nowrap font-semibold">{new Date(fromDate).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell />
                      <TableCell><Badge className="rounded-md text-[10px] bg-[#E0E7FF] text-[#4F46E5]" variant="outline">opening bal</Badge></TableCell>
                      <TableCell className="text-sm font-semibold">Opening Balance</TableCell>
                      <TableCell><Badge className="rounded-md text-[10px] bg-[#F4F4F5] text-[#52525B]" variant="outline">{filterAccount === 'all' ? 'All accounts' : accNameById(filterAccount)}</Badge></TableCell>
                      <TableCell className="text-sm text-[#52525B] italic">Balance carried forward from before this date range</TableCell>
                      <TableCell className="text-sm text-right font-mono text-emerald-700 font-semibold">{openingBalance > 0 ? fmt(openingBalance) : <span className="text-[#A1A1AA]">—</span>}</TableCell>
                      <TableCell className="text-sm text-right font-mono text-red-600 font-semibold">{openingBalance < 0 ? fmt(Math.abs(openingBalance)) : <span className="text-[#A1A1AA]">—</span>}</TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                  {dayBookRows.map(t => {
                    const isMerged = t._merged;
                    const isTransfer = t.type === 'transfer_in' || t.type === 'transfer_out';
                    const isGovtFee = t.type === 'govt_fee';                          // deduction — money paid to govt
                    const isGovtFeeCollected = t.type === 'govt_fee_collected';        // demoted slice of an invoice receipt
                    const isAdvanceReceived = t.type === 'advance_received';
                    const isAdvanceRefund = t.type === 'advance_refund';
                    const isOpening = t.type === 'opening_balance';
                    const isAdjustment = t.type === 'adjustment';
                    const isServiceCharge = t.type === 'income' && !!t.reference_id;   // demoted slice of an invoice receipt

                    // Receipts (money IN from customer) — all share the emerald palette.
                    const isReceiptKind = isMerged || isServiceCharge || isGovtFeeCollected || isAdvanceReceived;
                    // Payments (money OUT of our wallet) — red palette.
                    const isPaymentKind = isGovtFee || isAdvanceRefund;

                    const getBadgeClass = () => {
                      if (isReceiptKind) return 'bg-[#D1FAE5] text-[#10B981]';
                      if (isPaymentKind) return 'bg-[#FEE2E2] text-[#EF4444]';
                      if (isTransfer) return 'bg-[#FFFBEB] text-[#D97706]';
                      if (isOpening) return 'bg-[#E0E7FF] text-[#4F46E5]';
                      if (isAdjustment) return 'bg-[#F4F4F5] text-[#52525B]';
                      if (t.type === 'income') return 'bg-[#D1FAE5] text-[#10B981]';
                      return 'bg-[#FEE2E2] text-[#EF4444]';
                    };
                    const getBadgeLabel = () => {
                      if (isReceiptKind) return 'Receipt';
                      if (isPaymentKind) return 'Payment';
                      if (isTransfer) return 'transfer';
                      if (isOpening) return 'opening bal';
                      if (isAdjustment) return 'adjustment';
                      return t.type;
                    };

                    // Invoice-linked rows aren't directly editable here — edit the invoice instead.
                    const editable = !isMerged && !isServiceCharge && !isGovtFeeCollected && !isGovtFee;
                    const deletable = editable && (isAdmin || isTransfer);

                    return (
                      <TableRow key={t.id} className={`border-[#E4E4E7] ${bulkSelected.has(t.id) ? 'bg-[#FEF2F2]' : ''}`}>
                        <TableCell className="w-10">
                          {deletable ? (
                            <Checkbox
                              checked={bulkSelected.has(t.id)}
                              onCheckedChange={() => toggleRow(t.id)}
                              aria-label="Select transaction"
                              data-testid={`select-txn-${t.id}`}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell className="text-xs text-[#52525B] whitespace-nowrap">{new Date(t.created_at).toLocaleDateString('en-IN')}</TableCell>
                        <TableCell className="text-xs font-mono text-[#3B82F6]">
                          {t.voucher_number ? (
                            // Only invoice vouchers open the bill-view popup —
                            // RCP/TRF/INC/EXP rows already have inline edit.
                            String(t.voucher_number).startsWith('INV-') ? (
                              <button
                                type="button"
                                onClick={() => setViewBillNumber(t.voucher_number)}
                                className="hover:underline font-mono text-[#3B82F6] hover:text-[#2563EB] cursor-pointer"
                                data-testid={`view-bill-${t.voucher_number}`}
                              >
                                {t.voucher_number}
                              </button>
                            ) : (
                              t.voucher_number
                            )
                          ) : (
                            <span className="text-[#A1A1AA]">—</span>
                          )}
                        </TableCell>
                        <TableCell><Badge className={`rounded-md text-[10px] ${getBadgeClass()}`} variant="outline">{getBadgeLabel()}</Badge></TableCell>
                        <TableCell className="text-sm">{t.category}</TableCell>
                        <TableCell><Badge className="rounded-md text-[10px] bg-[#F4F4F5] text-[#52525B]" variant="outline">{accNameById(t.account)}</Badge></TableCell>
                        <TableCell className="text-sm text-[#52525B] max-w-[260px] truncate" title={t.description || ''}>{t.description || '-'}</TableCell>
                        <TableCell className="text-sm text-right font-mono text-emerald-700">{t.debit > 0 ? fmt(t.debit) : <span className="text-[#A1A1AA]">—</span>}</TableCell>
                        <TableCell className="text-sm text-right font-mono text-red-600">{t.credit > 0 ? fmt(t.credit) : <span className="text-[#A1A1AA]">—</span>}</TableCell>
                        <TableCell>
                          <div className="flex gap-0.5">
                            {editable && (
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#52525B] hover:text-[#3B82F6]" onClick={() => openEditTxn(t)} data-testid={`edit-txn-${t.id}`}><PencilSimple size={12} /></Button>
                            )}
                            {deletable && (
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#52525B] hover:text-[#EF4444]" onClick={() => setConfirmDelete(t)} data-testid={`delete-txn-${t.id}`}><Trash size={12} /></Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Closing balance pseudo-row — always visible when there are rows. */}
                  {(() => {
                    const totalDebit = dayBookRows.reduce((s, r) => s + (r.debit || 0), 0);
                    const totalCredit = dayBookRows.reduce((s, r) => s + (r.credit || 0), 0);
                    const closing = openingBalance + totalDebit - totalCredit;
                    return (
                      <TableRow className="border-[#E4E4E7] bg-[#F9FAFB]" data-testid="daybook-closing-row">
                        <TableCell />
                        <TableCell className="text-xs text-[#52525B] whitespace-nowrap font-semibold">{toDate ? new Date(toDate).toLocaleDateString('en-IN') : 'Now'}</TableCell>
                        <TableCell />
                        <TableCell><Badge className="rounded-md text-[10px] bg-[#E0E7FF] text-[#4F46E5]" variant="outline">closing bal</Badge></TableCell>
                        <TableCell className="text-sm font-semibold">Closing Balance</TableCell>
                        <TableCell><Badge className="rounded-md text-[10px] bg-[#F4F4F5] text-[#52525B]" variant="outline">{filterAccount === 'all' ? 'All accounts' : accNameById(filterAccount)}</Badge></TableCell>
                        <TableCell className="text-sm text-[#52525B] italic font-mono">Opening {fmt(openingBalance)} + Debit {fmt(totalDebit)} − Credit {fmt(totalCredit)}</TableCell>
                        <TableCell className="text-sm text-right font-mono text-emerald-700 font-semibold">{closing > 0 ? fmt(closing) : <span className="text-[#A1A1AA]">—</span>}</TableCell>
                        <TableCell className="text-sm text-right font-mono text-red-600 font-semibold">{closing < 0 ? fmt(Math.abs(closing)) : <span className="text-[#A1A1AA]">—</span>}</TableCell>
                        <TableCell />
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Trend Analysis moved to top of page */}

      {/* Edit Transaction Dialog */}
      <Dialog open={showEditTxn} onOpenChange={(v) => { setShowEditTxn(v); if (!v) setEditTxn(null); }}>
        <DialogContent className="rounded-xl border-[#E4E4E7]">
          <DialogHeader><DialogTitle>Edit Transaction</DialogTitle><DialogDescription>Update transaction details</DialogDescription></DialogHeader>
          <div className="space-y-4">
            {editTxn && ['income', 'expense'].includes(editTxn.type) && (
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B] mb-2 block">Type</Label>
                <div className="grid grid-cols-2 gap-2">
                  {['income', 'expense'].map(t => (
                    <Button key={t} variant={editTxnForm.type === t ? 'default' : 'outline'} className={`rounded-lg capitalize ${editTxnForm.type === t ? (t === 'income' ? 'bg-[#10B981] text-white hover:bg-[#10B981]/90 hover:text-white' : 'bg-[#EF4444] text-white hover:bg-[#EF4444]/90 hover:text-white') : 'border-[#E4E4E7]'}`}
                      onClick={() => setEditTxnForm({ ...editTxnForm, type: t })} data-testid={`edit-txn-type-${t}`}>
                      {t === 'income' ? <ArrowUp size={16} className="mr-2" /> : <ArrowDown size={16} className="mr-2" />}{t}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <div><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Date</Label><Input type="date" value={editTxnForm.date} onChange={e => setEditTxnForm({ ...editTxnForm, date: e.target.value })} className="rounded-lg mt-1" data-testid="edit-txn-date" /></div>
            <div><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Account</Label>
              <Select value={editTxnForm.account} onValueChange={v => setEditTxnForm({ ...editTxnForm, account: v })}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="edit-txn-account-select"><SelectValue /></SelectTrigger>
                <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Category</Label>
              <Input value={editTxnForm.category} onChange={e => setEditTxnForm({ ...editTxnForm, category: e.target.value })} className="rounded-lg mt-1" data-testid="edit-txn-category" />
            </div>
            <div><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Amount *</Label><Input type="number" value={editTxnForm.amount} onChange={e => setEditTxnForm({ ...editTxnForm, amount: e.target.value })} className="rounded-lg mt-1" data-testid="edit-txn-amount" /></div>
            <div><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Description</Label><Input value={editTxnForm.description} onChange={e => setEditTxnForm({ ...editTxnForm, description: e.target.value })} className="rounded-lg mt-1" data-testid="edit-txn-description" /></div>
          </div>
          <DialogFooter><Button onClick={handleEditTxn} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="update-transaction-btn">Update Transaction</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bill quick-view — fullscreen popup with Edit shortcut. */}
      <InvoiceViewDialog
        open={!!viewBillNumber}
        onOpenChange={(v) => { if (!v) setViewBillNumber(null); }}
        invoiceNumber={viewBillNumber}
      />

      {/* Confirm Delete */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Transaction</AlertDialogTitle>
            <AlertDialogDescription>Delete this {confirmDelete?.category} transaction of {fmt(confirmDelete?.amount || 0)}?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg" data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteTxn} className="bg-[#EF4444] text-white hover:bg-[#DC2626] rounded-lg" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Bulk Delete */}
      <AlertDialog open={confirmBulkDelete} onOpenChange={setConfirmBulkDelete}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {bulkSelected.size} transaction{bulkSelected.size > 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the selected entries from your Day Book and corresponding ledger accounts. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg" data-testid="cancel-bulk-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-[#EF4444] text-white hover:bg-[#DC2626] rounded-lg" data-testid="confirm-bulk-delete-btn">Delete {bulkSelected.size}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
